import { makeWordId } from "seneca-common/features/module/module-types/worked-example/logic/utils";
import { wordIsTested } from "seneca-common/features/module/module-types/worked-example/logic/utils/content";
import { wordIsCorrect } from "seneca-common/features/module/module-types/worked-example/logic/utils/score";
import {
  Content,
  Score,
  TestableWord
} from "seneca-common/features/module/module-types/worked-example/types";

import { AttemptingState, ModuleState, SkippedWordsForStep } from "./types";

export function getWordHasBeenSkipped(
  state: ModuleState,
  stepIndex: number,
  wordId: string
) {
  return (
    state.skippedWords &&
    state.skippedWords[stepIndex] &&
    state.skippedWords[stepIndex][wordId]
  );
}

export function getCurrentStepIndex(state: ModuleState) {
  return state.currentStepIndex;
}

export function stepTestingIsFinished({
  currentStepIndex,
  score,
  skippedWords
}: {
  currentStepIndex: number;
  score: Score;
  skippedWords: Array<SkippedWordsForStep>;
}) {
  const stepScore = score.steps[currentStepIndex];
  const stepSkippedWords = skippedWords[currentStepIndex];

  return Object.keys(stepScore).every(
    word => stepScore[word] || stepSkippedWords[word]
  );
}

export function lastWordForStepWasCorrect({
  moduleState: { currentStepIndex, attempting },
  score
}: {
  moduleState: ModuleState;
  score: Score;
}) {
  const stepScore = score.steps[currentStepIndex];

  if (attempting && attempting.stepIndex === currentStepIndex) {
    return stepScore[attempting.wordId];
  }

  // If no words are marked as attempting (i.e. inputs have been blurred), we can only take a guess as
  // to which word was attempted last. Naively we're going to guess that it's the last one in the sentence.

  const stepScoresForWords = Object.values(stepScore);

  return stepScoresForWords[stepScoresForWords.length - 1];
}

export function findNextTestedWord(
  score: Score,
  content: Content,
  moduleState: ModuleState
): AttemptingState {
  let nextTestedWord;

  content.steps.some(({ equation }, sIndex) => {
    return equation.some((wrd, wordIndex) => {
      if (
        wordIsTested(wrd) &&
        wordNeedsChecking(
          score,
          makeWordId((wrd as TestableWord).word, wordIndex),
          sIndex,
          moduleState
        )
      ) {
        nextTestedWord = {
          stepIndex: sIndex,
          wordId: makeWordId((wrd as TestableWord).word, wordIndex)
        };
        return true;
      }

      return false;
    });
  });

  if (!nextTestedWord) {
    throw new Error("Couldn't find next tested word");
  }

  return nextTestedWord;
}

function wordNeedsChecking(
  score: Score,
  wordId: string,
  stepIndex: number,
  { skippedWords }: ModuleState
): boolean {
  const wordHasBeenCompleted =
    wordIsCorrect({
      wordId,
      stepIndex,
      score
    }) || skippedWords[stepIndex][wordId];

  return !wordHasBeenCompleted;
}
